import React from "react";
import { MusicScale } from "../../../wasm/pkg/wasm";
import { AppContextType } from "../../AppContext";
import { ICON_CLOSE, ICON_COPY_TABS } from "../../constants/iconConstants";
import { ChordInScaleEdit, toShortChordNameOverBass } from "../../models/wasm.model";
import {
  createAndAddTabForEachInstrument,
  deleteTabsForChord,
  InstrumentSettingsMap,
  replaceTabForInstrument,
  TabsAll,
  updateTabsForInstrumentSettingsChange,
  updateTabsForSingleChordChange,
} from "../../statemachines/TablatureCrudInterface";
import InfoCard from "../layout_reuse/InfoCard";
import ChordEditor from "../music_controls/ChordEditor";
import IconButton from "../theme_reuse/IconButton";
import "./InstrumentationWorkspace.css";
import InstrumentCapoChoices from "./InstrumentCapoChoices";
import InstrumentTypeDropdown from "./InstrumentTypeDropdown";
import {
  buildDefaultTuningForInstrument,
  InstrumentType,
  isStringInstrument,
  StringsTuning,
  TablatureSingleChord,
} from "./InstrumentTypes.model";
import MonoSpacedTextColumn from "./MonoSpacedTextColumn";
import StringInstrumentChordTab, { toPrintableTab, toPrintableTabLetterColumn } from "./StringInstrumentChordTab";
import StringInstrumentNeck from "./StringInstrumentNeck";
import StringTuningColumn from "./StringTuningColumn";

export type ChordToBeInstrumented = {
  chord: ChordInScaleEdit;
  scale: MusicScale;
  uniqueId: number;
  // changeCounter: number;
};

/** mimicking RX/JS behavior, because there is no reason the top level app needs Instr Workspace data. */
interface Props {
  chordsToAdd: Array<ChordToBeInstrumented>;
  onChordAddSuccess: (uniqueId: number) => void;
}
interface State {
  /** store the chords added by the parent.  (pulling the state up seemed miserable - if this bites me, make a context) */
  chords: Array<ChordToBeInstrumented>;
  selectedIndex: number;
  /** for each instrument, an array with indices matching state.chords. */
  tabs: TabsAll;
  instrumentType: InstrumentType;
  instrumentSettings: InstrumentSettingsMap;
  isTabNumberingRelativeToCapo: boolean;
}

export default class InstrumentationWorkspace extends React.Component<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;

  constructor(props: Props) {
    super(props);
    this.state = {
      instrumentType: "Guit6",
      instrumentSettings: {
        Guit6: { stringsTuning: buildDefaultTuningForInstrument("Guit6"), capoPosition: 0 },
        Uke4: { stringsTuning: buildDefaultTuningForInstrument("Uke4"), capoPosition: 0 },
      },
      isTabNumberingRelativeToCapo: false,
      tabs: { Guit6: [], Uke4: [] },
      chords: [],
      selectedIndex: 0,
    };
  }

  componentDidUpdate(oldProps: Props) {
    if (oldProps.chordsToAdd.length !== this.props.chordsToAdd.length && this.props.chordsToAdd.length !== 0) {
      const { instrumentSettings } = this.state;
      const newChord = this.props.chordsToAdd[0];
      if (!this.state.chords.find((c) => c.uniqueId === newChord.uniqueId)) {
        const existingIndex = this.state.chords.findIndex(
          (x) => x.chord.bass_note_id === newChord.chord.bass_note_id && x.chord.chord_type === newChord.chord.chord_type
        );
        if (existingIndex === -1) {
          const chords = this.state.chords.concat([newChord]);
          const tabs = createAndAddTabForEachInstrument(this.state.tabs, instrumentSettings, newChord.chord);
          this.setState({ chords, tabs, selectedIndex: chords.length - 1 });
        } else {
          this.setState({ selectedIndex: existingIndex });
        }
      } else {
        console.warn("ignored prompt to add a chord twice (should never be triggered)");
      }
      this.props.onChordAddSuccess(newChord.uniqueId);
    }
  }

  handleUpdateTuning = (stringsTuning: StringsTuning) => {
    const { instrumentType } = this.state;
    const chordsAll = this.state.chords.map((c) => c.chord);
    const instrumentSettings = { ...this.state.instrumentSettings };
    instrumentSettings[instrumentType].stringsTuning = stringsTuning;
    const tabs = updateTabsForInstrumentSettingsChange(this.state.tabs, chordsAll, instrumentType, instrumentSettings);
    this.setState({ tabs, instrumentSettings });
  };

  handleChangeInstrumentType = (instrumentType: InstrumentType) => {
    // assumption: we already (always) generate tabs for all supported instruments, because matching array lengths is simpler
    this.setState({ instrumentType });
  };
  handleChangeCapoPosition = (capoPosition: number) => {
    const { instrumentType } = this.state;
    const chordsAll = this.state.chords.map((c) => c.chord);
    const instrumentSettings = { ...this.state.instrumentSettings };
    instrumentSettings[instrumentType].capoPosition = capoPosition;
    const tabs = updateTabsForInstrumentSettingsChange(this.state.tabs, chordsAll, instrumentType, instrumentSettings);
    this.setState({ tabs, instrumentSettings });
  };

  handleToggleRelativeTabNumbers = () => {
    const isTabNumberingRelativeToCapo = !this.state.isTabNumberingRelativeToCapo;
    this.setState({ isTabNumberingRelativeToCapo });
  };

  handlePromptUpdateChordMeta = (chordIndex: number) => {
    console.warn("the selected note is silenced in the chord voicing - you must first update and save the voicing.");
  };
  handleUpdateTabForSingleChord = (newTab: TablatureSingleChord, chordIndex: number) => {
    const { instrumentType } = this.state;
    const tabs = replaceTabForInstrument(this.state.tabs, instrumentType, newTab, chordIndex);
    this.setState({ tabs });
  };

  curryHandleSelectChord = (index: number) => () => {
    this.setState({ selectedIndex: index });
  };
  curryHandleDeleteChord = (index: number) => () => {
    let { selectedIndex } = this.state;
    const chords = this.state.chords.toSpliced(index, 1);
    const tabs = deleteTabsForChord(this.state.tabs, index);
    if (index === selectedIndex) {
      selectedIndex = 0;
    } else if (selectedIndex > chords.length - 1) {
      selectedIndex = chords.length - 1;
    }
    this.setState({ selectedIndex, chords, tabs });
  };
  handleUpdateCurrentChord = (chord: ChordInScaleEdit) => {
    const { selectedIndex: index, instrumentSettings } = this.state;
    const newChord = { ...this.state.chords[index], chord };
    const chords = this.state.chords.toSpliced(index, 1, newChord);
    const tabs = updateTabsForSingleChordChange(this.state.tabs, index, chords[index].chord, instrumentSettings);
    this.setState({ chords, tabs });
  };
  handleReset = () => {
    this.setState({ tabs: { Guit6: [], Uke4: [] }, chords: [], selectedIndex: 0 }); // don't reset instrument or capo
  };

  handleCopyTabsToClipboard = () => {
    if (this.state.tabs[this.state.instrumentType].length > 0) {
      const { isTabNumberingRelativeToCapo, instrumentSettings, instrumentType } = this.state;
      const { stringsTuning, capoPosition } = instrumentSettings[instrumentType];
      const scale = this.state.chords[0].scale;
      const letters = toPrintableTabLetterColumn(stringsTuning, scale);
      const tabsWithLines = this.state.tabs[instrumentType].map((t) => toPrintableTab(t, isTabNumberingRelativeToCapo, capoPosition));
      const linesOfTabs: Array<Array<string>> = tabsWithLines[0].map((_) => []);
      tabsWithLines.forEach((tab) => {
        tab.forEach((line, index) => {
          linesOfTabs[index].push(line);
        });
      });
      const chordNames = this.state.chords.map((c) => toShortChordNameOverBass(c.chord).replace(/\s/g, "")).join(" ");
      const combinedTabs = letters.map((letter, index) => {
        return letter + linesOfTabs[index].join("--");
      });
      const allText = ` ${chordNames}  \n${combinedTabs.join("\n")}`;
      const PLAIN_TEXT = "text/plain";
      const promise_text_blob = Promise.resolve(new Blob([allText], { type: PLAIN_TEXT }));
      const item = new ClipboardItem({ [PLAIN_TEXT]: promise_text_blob }, { presentationStyle: "unspecified" });
      window.navigator.clipboard.write([item]);
    }
  };

  render() {
    const { instrumentSettings, instrumentType, chords, tabs, selectedIndex, isTabNumberingRelativeToCapo } = this.state;
    const { stringsTuning, capoPosition } = instrumentSettings[instrumentType];
    const scale = chords[selectedIndex]?.scale || undefined;
    const currentChord = chords[selectedIndex]?.chord || undefined;
    const currentTab = tabs[instrumentType][selectedIndex] || undefined;
    if (!currentChord) {
      return undefined;
    }
    const tabLetters = toPrintableTabLetterColumn(stringsTuning, scale);

    return (
      <InfoCard includesHeaderBar className="area-instrumentation">
        <div className="info-card-header-bar">
          <div className="instrumentation-header-bar">
            <div className="instrumentation-header-bar-left-side">
              <InstrumentTypeDropdown instrumentType={instrumentType} onChange={this.handleChangeInstrumentType} />
              {isStringInstrument(instrumentType) && (
                <InstrumentCapoChoices
                  capoPosition={capoPosition}
                  isTabRelative={isTabNumberingRelativeToCapo}
                  onChangePosition={this.handleChangeCapoPosition}
                  onToggleRelativeTabNumbers={this.handleToggleRelativeTabNumbers}
                />
              )}
            </div>
            <div className="instrumentation-header-bar-right-side">
              <IconButton mini icon={ICON_COPY_TABS} title="Copy Tabs to Clipboard" onClick={this.handleCopyTabsToClipboard} />
              <IconButton mini icon={ICON_CLOSE} title="Clear Chord Selections" onClick={this.handleReset} />
            </div>
          </div>
        </div>
        <div className="info-card-body">
          <div className="instrumentation-tablature-frame">
            <div className="instrumentation-tablature-row">
              <div>
                <div className="instr-chords-top-left-spacer"> </div>
                <MonoSpacedTextColumn lines={tabLetters} uniqueId="letters" />
              </div>
              {tabs[instrumentType].map((t, index) => {
                // Potential for repeat chords makes key difficult.  Deletion makes simply using index infeasible.
                const key = `instr-chord-${chords[index].chord.root_note}-${chords[index].chord.chord_type}-${index}`;
                /* Print tabs in courier new */
                return (
                  <StringInstrumentChordTab
                    key={key}
                    uniqueId={key}
                    isSelected={index === selectedIndex}
                    chord={chords[index].chord}
                    tab={t}
                    onSelect={this.curryHandleSelectChord(index)}
                    onDelete={this.curryHandleDeleteChord(index)}
                    isTabNumberingRelativeToCapo={isTabNumberingRelativeToCapo}
                    capoPosition={capoPosition}
                  />
                );
              })}
            </div>
          </div>
          <div className="instrument-chord-deepdive-frame">
            <div className="instrumentation-chord-edit-frame">
              <ChordEditor chord={currentChord} onSave={this.handleUpdateCurrentChord} putButtonsOnTop />
            </div>
            <div className="instrumentation-stringed-instrument-frame">
              <div className="instrumentation-stringed-instrument">
                <StringTuningColumn scale={scale} tuning={stringsTuning} onUpdateTuning={this.handleUpdateTuning} />
                <StringInstrumentNeck
                  scale={scale}
                  tuning={stringsTuning}
                  capoPosition={capoPosition}
                  chord={currentChord}
                  chordIndex={selectedIndex}
                  onPromptUpdateChordMeta={this.handlePromptUpdateChordMeta}
                  onUpdateTabForSingleChord={this.handleUpdateTabForSingleChord}
                  tabSingleChord={currentTab}
                />
              </div>
            </div>
          </div>
        </div>
      </InfoCard>
    );
  }
}

InstrumentationWorkspace.contextType = AppContextType;
